import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Box, Paper, CircularProgress } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { useWizard } from '../hooks/useWizard';
import { useWizardNavigation } from '../hooks/useWizardNavigation';
import { useWizardStepData } from '../hooks/useWizardStepData';
import { useEmbeddedWizard } from '../hooks/useEmbeddedWizard';
import { getStepComponent, buildStepProps, getNavigationProps } from '../utils/wizardHelpers';
import WizardProgress from './WizardProgress';
import WizardNavigation from './WizardNavigation';
import WizardCompactToolbar from './WizardCompactToolbar';
import AnimatedStepSwitcher from '../../../components/common/AnimatedStepSwitcher';
import styles from './WizardContainer.module.scss';
import IntlMessages from 'util/IntlMessages';

const WizardContainer = ({
  onComplete,
  wizardType = 'integration-wizard-v2',
  wizardConfig = null,
  stepComponents = {},
  PlaceholderComponent = null,
  showTopNav = false,
}) => {
  const { currentShop } = useSelector((state) => state.app);

  const {
    state,
    data,
    currentStepIndex,
    currentStep,
    activeSteps,
    canGoPrev,
    isLastStep,
    goNext: goNextInternal,
    goPrev,
    goToStep,
    complete,
    loading,
    saving,
    error,
  } = useWizard(currentShop, wizardType, wizardConfig);

  const {
    stepData,
    hasStepData,
    isStepReady,
    handleStepUpdate,
    handleStepReady,
    handleRegisterSave,
    executeSave,
  } = useWizardStepData(currentStepIndex);

  const {
    saveAndNavigate,
    handleSaveCompletion,
    navigateNext,
  } = useWizardNavigation({
    currentShop,
    wizardType,
    currentStep,
    currentStepIndex,
    isLastStep,
    complete,
    onComplete,
    goNextInternal,
  });

  const {
    embeddedNavState,
    handleEmbeddedNext,
    handleEmbeddedPrev,
    handleEmbeddedComplete,
    handleEmbeddedNavChange,
    setEmbeddedWizardRefCallback,
  } = useEmbeddedWizard({
    currentStepIndex,
    isLastStep,
    complete,
    onComplete,
    goToStep,
    goPrev,
  });

  useEffect(() => {
    handleSaveCompletion(saving, error);
  }, [saving, error, handleSaveCompletion]);

  const isEmbeddedStep = currentStep?.id === 'component-configuration';

  const handleNext = useCallback(async () => {
    if (isEmbeddedStep) {
      handleEmbeddedNext();
      return;
    }

    try {
      await executeSave();
    } catch (e) {
      return;
    }

    if (hasStepData && currentStep?.id) {
      saveAndNavigate(stepData);
    } else {
      navigateNext();
    }
  }, [
    isEmbeddedStep,
    handleEmbeddedNext,
    executeSave,
    hasStepData,
    currentStep,
    stepData,
    saveAndNavigate,
    navigateNext,
  ]);

  const handlePrev = useCallback(() => {
    if (isEmbeddedStep) {
      handleEmbeddedPrev();
    } else {
      goPrev();
    }
  }, [isEmbeddedStep, handleEmbeddedPrev, goPrev]);

  if (!currentShop) {
    return (
      <Box className={styles.loading}>
        <Alert severity="warning"><IntlMessages id="ui.wizard.wizardContainer.pleaseSelectAShopToContinue" /></Alert>
      </Box>
    );
  }

  if (loading && !state) {
    return (
      <Box className={styles.loading}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  const StepComponent = getStepComponent(currentStep, stepComponents, PlaceholderComponent);
  const stepProps = buildStepProps({
    data,
    currentStep,
    currentShop,
    handleStepUpdate,
    handleStepReady,
    handleRegisterSave,
    handleEmbeddedComplete,
    handleEmbeddedNavChange,
    setEmbeddedWizardRefCallback,
    PlaceholderComponent,
    StepComponent,
  });

  const navProps = getNavigationProps({
    isEmbeddedStep,
    embeddedNavState,
    isStepReady,
    canGoPrev,
    isLastStep,
    saving,
  });

  return (
    <Box className={styles.root}>
      {!showTopNav && (
        <WizardProgress
          steps={activeSteps}
          currentStep={currentStepIndex}
          completedSteps={state?.completedSteps || []}
          onStepClick={goToStep}
        />
      )}

      {error && (
        <Alert severity="error" style={{ marginBottom: 24 }}>
          {error}
        </Alert>
      )}

      <Paper className={styles.paper}>
        {showTopNav && (
          <WizardCompactToolbar
            onPrev={handlePrev}
            onNext={handleNext}
            steps={activeSteps}
            currentStep={currentStepIndex}
            completedSteps={state?.completedSteps || []}
            onStepClick={goToStep}
            isEmbeddedStep={isEmbeddedStep}
            {...navProps}
          />
        )}
        <Box className={styles.stepContent}>
          <AnimatedStepSwitcher
            stepKey={currentStepIndex}
          >
            {StepComponent && <StepComponent {...stepProps} />}
          </AnimatedStepSwitcher>
        </Box>

        {!showTopNav && (
          <WizardNavigation
            onPrev={handlePrev}
            onNext={handleNext}
            {...navProps}
          />
        )}
      </Paper>
    </Box>
  );
};

WizardContainer.propTypes = {
  onComplete: PropTypes.func,
  wizardType: PropTypes.string,
  wizardConfig: PropTypes.shape({
    getActiveSteps: PropTypes.func,
    canProceed: PropTypes.func,
  }),
  stepComponents: PropTypes.objectOf(PropTypes.elementType),
  PlaceholderComponent: PropTypes.elementType,
  showTopNav: PropTypes.bool,
};

WizardContainer.defaultProps = {
  onComplete: null,
  wizardType: 'integration-wizard-v2',
  wizardConfig: null,
  stepComponents: {},
  PlaceholderComponent: null,
  showTopNav: false,
};

export default WizardContainer;
